import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { apiAuth } from "../utils/http";
import { STORAGE_KEYS, SESSION_MAX_AGE_MS, SESSION_RELOAD_STALE_MS } from "../utils/constants";

const SessionContext = createContext(null);

function readStored(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Two independent clocks, checked before anything else in this file reads
// storage: a hard cap on how long one QR scan's session may run for (so a
// session begun at the start of a meal isn't still live hours later), and a
// "did they actually come back" freshness check (so closing the tab for over
// an hour — a new customer may now be at that table — forces a rescan even
// though the 2-hour cap hasn't been hit yet). Either one tripping wipes the
// whole session; there's no partial expiry.
function enforceSessionExpiry() {
  const now = Date.now();
  const startedAt = readStored(STORAGE_KEYS.sessionStartedAt);
  const lastLoadedAt = readStored(STORAGE_KEYS.lastLoadedAt);

  const expired =
    (startedAt && now - startedAt > SESSION_MAX_AGE_MS) ||
    (lastLoadedAt && now - lastLoadedAt > SESSION_RELOAD_STALE_MS);

  if (expired) {
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  }

  localStorage.setItem(STORAGE_KEYS.lastLoadedAt, JSON.stringify(now));
  // Only set once per session — a fresh scan (nothing left after the clear
  // above) or the very first visit ever, never touched again until the next
  // expiry wipes it.
  if (!localStorage.getItem(STORAGE_KEYS.sessionStartedAt)) {
    localStorage.setItem(STORAGE_KEYS.sessionStartedAt, JSON.stringify(now));
  }
}

// This app is a single deployment (e.g. https://selforder.dotizo.com/) shared
// by every store, not one deployment per store — so the store can't be read
// from the hostname. Each store's QR/menu link instead carries ?url=<domain>,
// the same store_url column get-store (StoreController@getStoreDetails)
// matches on. Persisted immediately (like resolveTableKey does for ?table=)
// since stripSessionParamsFromUrl removes it from the address bar right
// after — a reload afterwards, or one before get-store's response has even
// come back, would otherwise have nothing left to resolve the store from.
// REACT_APP_DEV_STORE_URL is the last resort, for localhost where none of
// the above means anything.
function resolveStoreUrlParam() {
  const fromQuery = new URLSearchParams(window.location.search).get("url");
  if (fromQuery) {
    localStorage.setItem(STORAGE_KEYS.urlParam, fromQuery);
    return fromQuery;
  }
  return localStorage.getItem(STORAGE_KEYS.urlParam) || null;
}

function resolveStoreUrl() {
  const fromParam = resolveStoreUrlParam();
  if (fromParam) return fromParam;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return process.env.REACT_APP_DEV_STORE_URL || null;
  }
  // Falls back to whatever store was last resolved, for sessions cached
  // before urlParam existed as its own key.
  const cached = readStored(STORAGE_KEYS.store);
  return cached?.store_url || null;
}

// A Denzo POS's own "Tables" QR codes (denzo-pos-desktop's Local mode) carry
// this instead of url — see STORAGE_KEYS.storeIdParam. Persisted the same
// way and for the same reason (stripSessionParamsFromUrl removes it from
// the address bar right after a fresh scan). Absent from a normal cloud QR,
// which resolves by url instead — get-store accepts either.
function resolveStoreIdParam() {
  const fromQuery = new URLSearchParams(window.location.search).get("store_id");
  if (fromQuery) {
    localStorage.setItem(STORAGE_KEYS.storeIdParam, fromQuery);
    return fromQuery;
  }
  return localStorage.getItem(STORAGE_KEYS.storeIdParam) || null;
}

// The QR code on a table encodes ?table=<key> — the table's store_tables.key
// (e.g. "table1"), a stable string an admin sets when creating the table, NOT
// its numeric store_tables.id (see denzo-admin's Tables page, which prints
// this same QR). Scanning a different table's code must immediately take
// over as the active table, so this is re-read (and persisted) on every
// load, not just the first one. Away from a fresh scan (e.g. paging around
// the menu) the query param is absent and the last scanned table's key
// carries over from storage. The key still has to be resolved against the
// store's own store_tables list (see the `table` memo below) to get the
// numeric id placeOrder actually needs for store_table_id.
function resolveTableKey() {
  const fromQuery = new URLSearchParams(window.location.search).get("table");
  if (fromQuery) {
    localStorage.setItem(STORAGE_KEYS.tableKey, fromQuery);
    return fromQuery;
  }
  return localStorage.getItem(STORAGE_KEYS.tableKey) || null;
}

// The QR link's ?url=/?table= only need to survive long enough to be cached
// above — left in the address bar afterwards they're confusing (the page
// looks like it's still mid-scan) and a plain refresh would re-capture them
// as if freshly scanned instead of falling back to what's already cached.
function stripSessionParamsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("url") && !params.has("table") && !params.has("store_id")) return;
  params.delete("url");
  params.delete("table");
  params.delete("store_id");
  const query = params.toString();
  const next = window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
  window.history.replaceState(window.history.state, "", next);
}

export function SessionProvider({ children }) {
  // Order matters here: expiry has to run before any of the reads below, and
  // resolving+stripping the QR's params has to happen before this component's
  // first paint so a refresh never sees them still sitting in the address bar.
  const [store, setStore] = useState(() => {
    enforceSessionExpiry();
    return readStored(STORAGE_KEYS.store);
  });
  const [user, setUser] = useState(() => readStored(STORAGE_KEYS.user));
  const [tableKey, setTableKey] = useState(resolveTableKey);
  useState(() => {
    resolveStoreUrlParam();
    resolveStoreIdParam();
    stripSessionParamsFromUrl();
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Plain state (not just a localStorage read on every render) so that
  // saving a contact number after the first checkout is immediately visible
  // to consumers — e.g. Cart.js pre-filling these fields the next time the
  // guest opens the cart, without needing some unrelated re-render to notice
  // the localStorage write.
  const [contactNumber, setContactNumber] = useState(() => readStored(STORAGE_KEYS.contactNumber) || "");
  const [customerName, setCustomerName] = useState(() => readStored(STORAGE_KEYS.customerName) || "");

  const saveContactDetails = useCallback((name, contact) => {
    localStorage.setItem(STORAGE_KEYS.customerName, JSON.stringify(name || ""));
    localStorage.setItem(STORAGE_KEYS.contactNumber, JSON.stringify(contact || ""));
    setCustomerName(name || "");
    setContactNumber(contact || "");
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError("");
      try {
        const storeUrl = resolveStoreUrl();
        const storeId = resolveStoreIdParam();
        if (!storeUrl && !storeId) throw new Error("Missing store — this link needs a ?url= or ?store_id= parameter.");

        // GET v1/get-store?url=<domain>&store_id=<id> — public, no auth
        // needed. store_id (when the QR carried one) is checked first on
        // the server, url as the fallback either way — see
        // StoreController@getStoreDetails.
        const res = await apiAuth.get("get-store", { params: { url: storeUrl, store_id: storeId } });
        const storeData = res.data?.store;
        if (!storeData) throw new Error("Store not found");
        if (cancelled) return;
        localStorage.setItem(STORAGE_KEYS.store, JSON.stringify(storeData));
        setStore(storeData);

        // Re-use an existing guest token for this same store; a token minted
        // for a different store (device previously used at another
        // restaurant) must not be reused as-is.
        const existingUser = readStored(STORAGE_KEYS.user);
        const existingToken = localStorage.getItem(STORAGE_KEYS.token);
        if (existingToken && existingUser?.store_id === storeData.id) {
          setLoading(false);
          return;
        }

        // POST v1/register — StoreController@register — mints a fresh
        // anonymous guest (DenzoUser) scoped to this store and its token.
        const regRes = await apiAuth.post("register", { store_id: storeData.id });
        if (cancelled) return;
        localStorage.setItem(STORAGE_KEYS.token, JSON.stringify(regRes.data?.token));
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(regRes.data?.user));
        setUser(regRes.data?.user);
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load this store's menu right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const currency = store?.currency || "";

  // The one lookup that turns the QR's printed key into the numeric id
  // placeOrder needs. null while the store hasn't loaded yet, and also null
  // (rather than throwing) if the key doesn't match any table — a stale QR
  // code, a deactivated table's area, or a typo'd link land here rather than
  // crashing the app; OrderContext.submitOrder is what surfaces that to the
  // guest, at the point it actually matters.
  const table = useMemo(() => {
    if (!tableKey || !store?.store_tables) return null;
    return store.store_tables.find((t) => String(t.key) === String(tableKey)) || null;
  }, [store, tableKey]);

  return (
    <SessionContext.Provider
      value={{
        store,
        user,
        currency,
        tableKey,
        setTableKey,
        table,
        contactNumber,
        customerName,
        saveContactDetails,
        loading,
        error,
        ready: !loading && !error && !!store,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}
